import React, { useState } from "react";
import { Search, X, Download, FileJson, RefreshCw, Loader2 } from "lucide-react";
import { exportCSV, exportJSON } from "../../lib/export";
import { refreshSource } from "../../lib/api";

export default function FilterBar({
  search,
  setSearch,
  agency,
  setAgency,
  type,
  setType,
  country,
  setCountry,
  classification,
  setClassification,
  stats,
  onClear,
  resultsCount,
  cases,
  onRefreshComplete,
}) {
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState(null);

  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshMsg(null);
    try {
      const r = await refreshSource();
      if (r.ok) {
        const newCount = r.new_case_ids?.length || 0;
        const newImgs = r.new_image_urls?.length || 0;
        setRefreshMsg(
          `Synced · ${r.discovered_slideshow_count} slideshow assets · ${newCount} new case ID(s) · ${newImgs} new image(s)`
        );
      } else {
        setRefreshMsg(`Source unreachable: ${r.error}`);
      }
      onRefreshComplete?.(r);
    } catch (e) {
      const reason =
        e?.response?.status
          ? `HTTP ${e.response.status} · ${e.response.statusText || ""}`
          : e?.message || "Network error";
      // Surface enough that the user can act / report it
      console.error("[refresh]", e);
      setRefreshMsg(`Refresh failed · ${reason}`);
    } finally {
      setRefreshing(false);
      setTimeout(() => setRefreshMsg(null), 12000);
    }
  };

  const agencies = stats?.by_agency?.map((a) => a.label) ?? [];
  const types = stats?.by_type?.map((t) => t.label) ?? [];
  const countries = stats?.by_country?.map((c) => c.label) ?? [];

  const Select = ({ value, onChange, options, label, testid }) => (
    <select
      data-testid={testid}
      value={value || ""}
      onChange={(e) => onChange(e.target.value || null)}
      className="bg-zinc-950 border border-zinc-800 px-3 py-2 font-mono text-xs uppercase tracking-widest text-zinc-300 hover:border-zinc-700 focus:border-amber-400 focus:outline-none focus:ring-0 cursor-pointer"
    >
      <option value="">ALL {label}</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );

  const hasFilter =
    search || agency || type || country || classification;

  return (
    <div className="border-b border-zinc-800 bg-zinc-950">
      <div className="flex flex-col md:flex-row md:items-center gap-3 px-6 py-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
          <input
            data-testid="case-search-input"
            value={search || ""}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by description, location, case ID, sensor…"
            className="w-full bg-zinc-950 border border-zinc-800 pl-9 pr-3 py-2 font-mono text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-amber-400 focus:outline-none"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Select
            value={agency}
            onChange={setAgency}
            options={agencies}
            label="Agencies"
            testid="filter-agency"
          />
          <Select
            value={type}
            onChange={setType}
            options={types}
            label="Types"
            testid="filter-type"
          />
          <Select
            value={country}
            onChange={setCountry}
            options={countries}
            label="Regions"
            testid="filter-country"
          />
          <Select
            value={classification}
            onChange={setClassification}
            options={["Unresolved", "Resolved"]}
            label="Status"
            testid="filter-classification"
          />
          {hasFilter && (
            <button
              data-testid="clear-filters-btn"
              onClick={onClear}
              className="border border-zinc-800 px-3 py-2 font-mono text-xs uppercase tracking-widest text-zinc-400 hover:text-amber-400 hover:border-amber-400/60 flex items-center gap-1.5"
            >
              <X className="h-3 w-3" />
              CLEAR
            </button>
          )}
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 md:ml-auto flex items-center gap-3">
          <span className="text-zinc-300" data-testid="results-count">
            {String(resultsCount ?? 0).padStart(3, "0")}
          </span>{" "}
          RESULTS
          <span className="h-4 w-px bg-zinc-800" />
          <button
            data-testid="export-csv-btn"
            onClick={() => exportCSV(cases)}
            disabled={!cases?.length}
            className="flex items-center gap-1 px-2 py-1 border border-zinc-800 hover:border-amber-400/60 hover:text-amber-400 disabled:opacity-40 transition-colors"
          >
            <Download className="h-3 w-3" /> CSV
          </button>
          <button
            data-testid="export-json-btn"
            onClick={() => exportJSON(cases)}
            disabled={!cases?.length}
            className="flex items-center gap-1 px-2 py-1 border border-zinc-800 hover:border-amber-400/60 hover:text-amber-400 disabled:opacity-40 transition-colors"
          >
            <FileJson className="h-3 w-3" /> JSON
          </button>
          <button
            data-testid="refresh-source-btn"
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1 px-2 py-1 border border-zinc-800 hover:border-amber-400/60 hover:text-amber-400 disabled:opacity-40 transition-colors"
            title="Pull fresh slideshow imagery from war.gov/UFO"
          >
            {refreshing ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3" />
            )}
            SYNC
          </button>
        </div>
      </div>
      {refreshMsg && (
        <div
          data-testid="refresh-msg"
          className="px-6 pb-3 -mt-1 font-mono text-[10px] uppercase tracking-widest text-amber-400/90"
        >
          ▸ {refreshMsg}
        </div>
      )}
    </div>
  );
}
